/**
 * jw-product-state.js
 * Phase F2.2 — Runtime State Manager
 *
 * 职责：管理用户运行时可变的 UI 状态
 *
 * 输入：window.JWProductContext (optional, for init)
 * 输出：window.JWProductState
 *
 * Contract:
 *   - State 只保存用户运行时变化的数据（activeSku、galleryIndex、qtys 等）
 *   - product/images/binding 属于 Context，不重复存储
 *   - 向后兼容现有 S.mi / S.activeSku / S.qtys 等内联引用
 *
 * 🚫 禁止：
 *   - 存储产品数据（属于 Context）
 *   - 计算价格（属于 Variant/F2.3）
 *   - 图片解析（属于 ImageResolver）
 */
(function () {
  'use strict';

  var _state = null;
  var _listeners = {};

  /**
   * Emit event to registered listeners.
   */
  function emit(event, payload) {
    var fns = _listeners[event];
    if (!fns || !fns.length) return;
    fns.forEach(function (fn) {
      try { fn(payload); } catch (e) { /* silent */ }
    });
  }

  /**
   * Initialize state from JWProductContext (optional).
   * Returns a mutable state object backward-compatible with old `var S`.
   *
   * @param {object} context — JWProductContext or null
   * @returns {object} state — flat mutable object
   */
  function init(context) {
    var ctx = context || {};

    // ── Core runtime state (matches old `var S` keys exactly) ──
    _state = {
      // Gallery
      mi: 0,                          // main image index

      // Variant
      activeSku: null,                // active SKU ID
      activeVariantId: null,          // reserved for F2.3 (Shopify variant ID)

      // Viewer
      vIdx: 0,                        // viewer image index
      vList: [],                      // viewer image list
      vScale: 1,                      // viewer zoom scale

      // Pricing
      qtys: {},                       // { skuId: quantity }
      mobTierActive: 0,               // mobile tier highlight index

      // Context snapshot (read-only reference, for convenience)
      _context: ctx
    };

    emit('init', _state);
    return _state;
  }

  /**
   * Get current state (or null if not initialized).
   */
  function getState() {
    return _state;
  }

  /**
   * Update a single state key and notify subscribers.
   *
   * @param {string} key   — state key (e.g. 'activeSku', 'mi')
   * @param {*}      value — new value
   */
  function set(key, value) {
    if (!_state) return;
    var old = _state[key];
    _state[key] = value;
    emit('change', { key: key, oldValue: old, newValue: value });
    emit('change:' + key, { oldValue: old, newValue: value });
  }

  /**
   * Subscribe to state events.
   *
   * Events:
   *   'init'          — state initialized
   *   'change'        — any key changed: { key, oldValue, newValue }
   *   'change:<key>'  — specific key changed: { oldValue, newValue }
   *
   * @param {string}   event
   * @param {function} handler
   */
  function on(event, handler) {
    if (!_listeners[event]) _listeners[event] = [];
    _listeners[event].push(handler);
  }

  /**
   * Reset state to initial values.
   * Keeps existing context reference.
   */
  function reset() {
    if (!_state) return;
    var ctx = _state._context;
    _state.mi = 0;
    _state.activeSku = null;
    _state.activeVariantId = null;
    _state.vIdx = 0;
    _state.vList = [];
    _state.vScale = 1;
    _state.qtys = {};
    _state.mobTierActive = 0;
    emit('reset', _state);
  }

  // ── Expose ──
  window.JWProductState = {
    init: init,
    get: getState,
    set: set,
    on: on,
    reset: reset
  };

})();
